<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { NButton, NSkeleton } from 'naive-ui'
import { storeToRefs } from 'pinia'
import { useSourceActivityStore } from '../stores/source_activity'
import ActivityRow from './ActivityRow.vue'

// The activity-list half of AddToWikiDrawer.vue (#1099) — shared
// across all three tabs (Ask/File/URL), so it owns its own subscription
// to the source_activity store rather than receiving rows as props.
// Extracted so the parent stays under the 300-line hard limit; no
// behavior change from the inline version.
const emit = defineEmits<{
  (e: 'open-path', path: string): void
}>()

const activity = useSourceActivityStore()
const { active, recentTerminal, loading: activityLoading, loadError: activityError, queueDepth } = storeToRefs(activity)

// Cap the "Active" list to a screenful; anything beyond that collapses
// into an "and N more running…" line instead of growing the drawer.
const MAX_VISIBLE_ACTIVE = 5

const pagedActive = computed(() => active.value.slice(0, MAX_VISIBLE_ACTIVE))
const overflowCount = computed(() => Math.max(0, active.value.length - MAX_VISIBLE_ACTIVE))

onMounted(() => {
  void activity.load()
})
</script>

<template>
  <!-- Activity list: shared across all tabs. -->
  <section class="mt-8 flex flex-col gap-4" data-testid="activity-list">
    <div
      v-if="activityLoading && !active.length && !recentTerminal.length"
      class="flex flex-col gap-2"
      data-testid="activity-skeleton"
    >
      <NSkeleton :repeat="2" class="!h-12 !rounded" />
    </div>

    <div v-else-if="activityError" class="flex flex-col gap-2 text-sm text-red-300" data-testid="activity-error">
      <span>Couldn't load recent activity: {{ activityError }}</span>
      <NButton size="small" @click="activity.load()">Retry</NButton>
    </div>

    <template v-else>
      <section v-if="active.length" class="flex flex-col gap-2" data-testid="activity-active">
        <h3 class="text-xs font-semibold uppercase tracking-widest text-white/40 m-0">Active ({{ active.length }})</h3>
        <ul class="list-none p-0 m-0 flex flex-col gap-2">
          <ActivityRow
            v-for="r in pagedActive"
            :key="r.id"
            :row="r"
            :queue-position="activity.queuePositionFor(r.id)"
            :queue-depth="queueDepth"
            @open-path="emit('open-path', $event)"
          />
        </ul>
        <p v-if="overflowCount > 0" class="text-xs text-white/40 m-0">and {{ overflowCount }} more running…</p>
      </section>

      <section v-if="recentTerminal.length" class="flex flex-col gap-2" data-testid="activity-recent">
        <h3 class="text-xs font-semibold uppercase tracking-widest text-white/40 m-0">Recent (last 24h)</h3>
        <ul class="list-none p-0 m-0 flex flex-col gap-2">
          <ActivityRow v-for="r in recentTerminal" :key="r.id" :row="r" @open-path="emit('open-path', $event)" />
        </ul>
      </section>

      <p v-if="!active.length && !recentTerminal.length" class="text-sm text-white/40 m-0">
        Nothing in flight, and no activity in the last 24h.
      </p>
    </template>
  </section>
</template>
